'use client';

import React, { useEffect, useRef, useState } from 'react';
import type { ComboBoxProps } from './comboBox.types';
import { Button, Input } from '@ui';
import { IconCloseX } from '@icons';

const DEFAULT_ID = 'combobox';

const ComboBox: React.FC<ComboBoxProps> = ({
  items,
  idPrefix = DEFAULT_ID,
  placeholder,
  multi = false,
  className,
  defaultValue,
  onChange,
}) => {
  const [query, setQuery] = useState('');
  const [list, setList] = useState<string[]>(items.slice());
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [selected, setSelected] = useState<string[]>(
    Array.isArray(defaultValue)
      ? defaultValue
      : typeof defaultValue === 'string' && !multi
        ? [defaultValue]
        : []
  );

  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const holderRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // If a single defaultValue is provided, initialize the input display and query
    if (!multi && typeof defaultValue === 'string' && defaultValue) {
      if (inputRef.current) inputRef.current.value = defaultValue;
      setQuery(defaultValue);
      // keep list filtered accordingly (effect above watches query)
    }

    const s = query.trim().toLowerCase();
    setList(
      s
        ? items.filter((i) => i.toLowerCase().includes(s) && !selected.includes(i))
        : items.filter((i) => !selected.includes(i))
    );
    setActive(0);
  }, [query, items, selected, defaultValue, multi]);

  useEffect(() => {
    if (onChange) {
      if (multi) onChange(selected);
      else onChange(selected[0] ?? '');
    }
  }, [selected, multi, onChange]);

  function choose(val: string) {
    if (multi) {
      setSelected((s) => (s.includes(val) ? s : [...s, val]));
      if (inputRef.current) inputRef.current.value = '';
      setQuery('');
      setOpen(true);
      if (inputRef.current) inputRef.current.focus();
    } else {
      setSelected([val]);
      if (inputRef.current) inputRef.current.value = val;
      setQuery(val);
      setOpen(false);
    }
  }

  function remove(val: string) {
    setSelected((s) => s.filter((x) => x !== val));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (
      e.key === 'Backspace' &&
      multi &&
      inputRef.current &&
      inputRef.current.value === '' &&
      selected.length
    ) {
      setSelected((s) => s.slice(0, -1));
      e.preventDefault();
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (list.length ? (a + 1) % list.length : 0));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (list.length ? (a - 1 + list.length) % list.length : 0));
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (list[active]) choose(list[active]);
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  useEffect(() => {
    // Close combobox when clicking outside
    function onDocMouse(e: MouseEvent) {
      const el = rootRef.current;
      if (!el) return;
      const target = e.target as Node | null;
      if (target && !el.contains(target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocMouse);
    return () => document.removeEventListener('mousedown', onDocMouse);
  }, [multi]);

  return (
    <div className={`combo ${className ?? ''}`} data-combo ref={rootRef}>
      <label htmlFor={`${idPrefix}-input`} className='hint'>
        {multi ? 'Select items' : 'Choose an item'}
      </label>
      <div
        className={`combo-field${multi ? ' chips' : ''}${
          multi && selected.length ? ' has-chips' : ''
        }`}
        role='combobox'
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-controls={`${idPrefix}-list`}
      >
        <div className='chip-holder' ref={holderRef}>
          {multi &&
            selected.map((s) => (
              <span key={s} className='chip'>
                {s}
                <Button
                  label={''}
                  icon={<IconCloseX />}
                  className={'btn-ghost btn-icon  btn-round btn-xs remove'}
                  aria-label='Remove'
                  onClick={() => remove(s)}
                />
              </span>
            ))}
        </div>
        <Input
          id={`${idPrefix}-input`}
          ref={inputRef}
          className='combo-input'
          autoComplete='off'
          placeholder={placeholder}
          onInput={(e) => {
            const v = (e.target as HTMLInputElement).value;
            setQuery(v);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {!multi && selected[0] && (
          <Button
            label={''}
            icon={<IconCloseX />}
            className='btn-ghost btn-icon combo-clear'
            aria-label='Clear'
            onClick={() => {
              if (inputRef.current) inputRef.current.value = '';
              setQuery('');
              setSelected([]);
              setOpen(true);
            }}
          />
        )}
      </div>
      <ul className='combo-list' id={`${idPrefix}-list`} role='listbox' ref={listRef}>
        {list.map((v, i) => (
          <li
            key={v}
            className={`combo-item ${i === active ? 'is-active' : ''}`}
            role='option'
            aria-selected={i === active}
            data-val={v}
            onClick={() => choose(v)}
          >
            {v}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ComboBox;
